import React, { useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLink,
    faCopy,
    faCheck,
    faArrowRight,
    faShare,
    faCoins,
    faUsers,
    faShoppingBag,
    faExclamationTriangle,
    faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Header from "../../components/Header";
import FooterV2 from "../../components/FooterV2";
import AffiliateFraudPolicyNote from "../../components/AffiliateFraudPolicyNote";
import shopeApi from "../../api/shopeeApi";
import lazadaApi from "../../api/lazadaApi";
import shopeefoodApi from "../../api/shopeefoodApi";
import { LinkSource } from "../../constants/link-source";

// Detect platform from URL
function detectPlatform(url) {
    if (!url) return null;
    const lower = url.toLowerCase();
    // ShopeeFood xét TRƯỚC Shopee: link food cũng chứa chuỗi "shopee"
    if (
        lower.includes("shopeefood") ||
        lower.includes("spf.shopee.vn") ||
        lower.includes("now-food")
    )
        return "shopeefood";
    if (
        lower.includes("shopee") ||
        lower.includes("s.shopee.vn") ||
        lower.includes("shp.ee")
    )
        return "shopee";
    if (lower.includes("lazada") || lower.includes("lzd.co"))
        return "lazada";
    return null;
}

// Platform display info
const PLATFORM_INFO = {
    shopee: {
        name: "Shopee",
        color: "from-brand-coral to-brand-warm",
        textColor: "text-orange-600",
        bgColor: "bg-orange-50",
        borderColor: "border-orange-200",
    },
    lazada: {
        name: "Lazada",
        color: "from-[#0dccff] to-[#4760ff]",
        textColor: "text-blue-600",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200",
    },
    shopeefood: {
        name: "ShopeeFood",
        color: "from-[#ff7a00] to-[#ffb020]",
        textColor: "text-orange-600",
        bgColor: "bg-orange-50",
        borderColor: "border-orange-200",
    },
};

function LinkConverter() {
    const { isLogined, userInfo } = useSelector((state) => state.auth);
    const [inputLink, setInputLink] = useState("");
    const [resultLink, setResultLink] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);
    const [platform, setPlatform] = useState(null);
    const convertingRef = useRef(false);

    const handleConvert = useCallback(async (overrideLink) => {
        const raw = typeof overrideLink === "string" ? overrideLink : inputLink;
        const trimmed = raw.trim();
        if (!trimmed) {
            setError("Vui lòng dán link sản phẩm");
            return;
        }

        const detected = detectPlatform(trimmed);
        if (!detected) {
            setError("Chỉ hỗ trợ link Shopee, ShopeeFood hoặc Lazada");
            return;
        }

        if (convertingRef.current) return;
        convertingRef.current = true;
        setPlatform(detected);
        setError("");
        setResultLink("");
        setLoading(true);

        try {
            let affLink = null;

            if (detected === "shopee") {
                const { data } = await shopeApi.convertLink(trimmed, LinkSource.Converter);
                affLink = data?.data?.batchCustomLink?.[0]?.shortLink;
            } else if (detected === "shopeefood") {
                const { data } = await shopeefoodApi.getLink(trimmed, LinkSource.Converter);
                affLink = data?.data?.link;
            } else if (detected === "lazada") {
                const { data } = await lazadaApi.getLinkAffByUrl(trimmed, {}, LinkSource.Converter);
                affLink = data?.linkAff?.shortLink || data?.data?.shortLink || data?.data?.batchCustomLink?.[0]?.shortLink;
            }

            if (affLink) {
                setResultLink(affLink);
            } else {
                setError("Không thể tạo link. Vui lòng thử lại.");
            }
        } catch (err) {
            const msg = err?.response?.data?.message || "Có lỗi xảy ra. Vui lòng thử lại.";
            setError(msg);
        } finally {
            setLoading(false);
            convertingRef.current = false;
        }
    }, [inputLink]);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(resultLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [resultLink]);

    const handleShare = useCallback(() => {
        if (navigator.share) {
            navigator.share({ text: resultLink }).catch(() => {});
        } else {
            handleCopy();
        }
    }, [resultLink, handleCopy]);

    // Auto-convert when a valid link is pasted or typed
    const autoConvert = useCallback((text) => {
        if (detectPlatform(text)) {
            handleConvert(text);
        }
    }, [handleConvert]);

    const handlePaste = useCallback(async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (text) {
                setInputLink(text);
                autoConvert(text.trim());
            }
        } catch {
            // Clipboard API not available
        }
    }, [autoConvert]);

    return (
        <div className="min-h-dvh flex flex-col bg-surface-sunken">
            <Header />
            <div className="flex-1 pt-16 pb-20 md:w-[768px] lg:w-[1024px] md:mx-auto w-full mt-4">
                {/* Hero — brand gradient (đồng bộ tokens), icon badge + text */}
                <div className="bg-gradient-to-br from-brand-red to-brand-warm text-white px-4 py-4 rounded-t-lg flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-surface/20 flex items-center justify-center shrink-0">
                        <FontAwesomeIcon icon={faCoins} className="text-lg" />
                    </div>
                    <div>
                        <h1 className="font-lexend text-lg font-bold leading-tight">Rải link lụm tiền</h1>
                        <p className="text-white/85 text-xs mt-0.5">
                            Dán link → nhận link kiếm tiền → bạn bè mua → bạn nhận hoa hồng
                        </p>
                    </div>
                </div>

                {/* Main Card */}
                <div className="bg-surface p-4">
                        {/* Login notice */}
                        {!isLogined && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                                <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    className="text-amber-500 mt-0.5"
                                />
                                <div className="text-sm">
                                    <p className="font-semibold text-amber-700">
                                        Bạn chưa đăng nhập
                                    </p>
                                    <p className="text-amber-600">
                                        <Link to="/login" className="underline font-semibold">
                                            Đăng nhập
                                        </Link>{" "}
                                        để link tự gắn ID của bạn và nhận hoa hồng.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* User badge */}
                        {isLogined && userInfo?.name && (
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2">
                                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                    {userInfo.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="text-sm">
                                    <p className="font-semibold text-green-800">
                                        {userInfo.name}
                                    </p>
                                    <p className="text-green-600 text-xs">
                                        Link sẽ tự gắn ID #{userInfo.userId} của bạn
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Input Section */}
                        <label className="text-sm font-semibold text-content-2 mb-2 block">
                            Dán link sản phẩm
                        </label>
                        <div className="flex gap-2">
                            <div className="flex-1 relative">
                                <input
                                    type="text"
                                    value={inputLink}
                                    onChange={(e) => {
                                        setInputLink(e.target.value);
                                        setError("");
                                    }}
                                    onPaste={(e) => {
                                        const text = e.clipboardData.getData("text");
                                        if (text) autoConvert(text.trim());
                                    }}
                                    onKeyDown={(e) => e.key === "Enter" && handleConvert()}
                                    placeholder="Link Shopee / ShopeeFood / Lazada"
                                    className="w-full border border-line-strong rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-coral focus:border-transparent pr-16"
                                />
                                <button
                                    onClick={handlePaste}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-brand-red font-semibold bg-red-50 px-3 py-1.5 rounded-lg hover:bg-red-100 active:scale-95 transition-all"
                                >
                                    Dán
                                </button>
                            </div>
                        </div>

                        {/* Platform badge */}
                        {inputLink && detectPlatform(inputLink) && (
                            <div className="mt-2">
                                <span
                                    className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                                        PLATFORM_INFO[detectPlatform(inputLink)]?.bgColor
                                    } ${PLATFORM_INFO[detectPlatform(inputLink)]?.textColor}`}
                                >
                                    {PLATFORM_INFO[detectPlatform(inputLink)]?.name}
                                </span>
                            </div>
                        )}

                        {/* Convert Button */}
                        <button
                            onClick={handleConvert}
                            disabled={loading || !inputLink.trim()}
                            className={`w-full mt-4 py-3.5 rounded-xl font-bold text-white text-sm transition-all active:scale-[0.98] ${
                                loading || !inputLink.trim()
                                    ? "bg-surface-raised cursor-not-allowed"
                                    : "bg-gradient-to-r from-brand-red to-brand-warm shadow-md hover:shadow-lg"
                            }`}
                        >
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    Đang tạo link...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    <FontAwesomeIcon icon={faArrowRight} />
                                    Tạo Link Hoàn Tiền
                                </span>
                            )}
                        </button>

                        {/* Điều khoản chống gian lận TTLK — ngay dưới nút tạo link */}
                        <AffiliateFraudPolicyNote className="mt-3" />

                        {/* Error */}
                        {error && (
                            <p className="mt-3 text-sm text-red-500 text-center">{error}</p>
                        )}

                        {/* Result */}
                        {resultLink && (
                            <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                                <p className="text-xs font-semibold text-green-700 mb-2">
                                    Link hoàn tiền của bạn
                                </p>
                                <div className="bg-surface rounded-lg p-3 border border-green-100 break-all text-sm text-content select-all">
                                    {resultLink}
                                </div>
                                <div className="flex gap-2 mt-3">
                                    <button
                                        onClick={handleCopy}
                                        className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] ${
                                            copied
                                                ? "bg-green-500 text-white"
                                                : "bg-green-600 text-white hover:bg-green-700"
                                        }`}
                                    >
                                        <FontAwesomeIcon
                                            icon={copied ? faCheck : faCopy}
                                            className="mr-2"
                                        />
                                        {copied ? "Đã copy!" : "Copy Link"}
                                    </button>
                                    <button
                                        onClick={handleShare}
                                        className="px-5 py-2.5 rounded-xl font-semibold text-sm bg-blue-500 text-white hover:bg-blue-600 active:scale-[0.98] transition-all"
                                    >
                                        <FontAwesomeIcon icon={faShare} />
                                    </button>
                                </div>
                            </div>
                        )}
                </div>

                {/* How it works */}
                <div className="bg-surface p-4 mt-2 rounded-lg">
                        <h2 className="font-bold text-base mb-4">Cách hoạt động</h2>
                        <div className="flex flex-col gap-4">
                            <StepItem
                                step={1}
                                icon={faLink}
                                color="bg-brand-warm"
                                title="Dán link sản phẩm"
                                desc="Copy link sản phẩm từ Shopee hoặc Lazada và dán vào ô bên trên"
                            />
                            <StepItem
                                step={2}
                                icon={faShare}
                                color="bg-brand-coral"
                                title="Chia sẻ link mới"
                                desc="Chia sẻ link hoàn tiền lên Facebook, Zalo, Telegram hoặc group Longhousee"
                            />
                            <StepItem
                                step={3}
                                icon={faCoins}
                                color="bg-brand-red"
                                title="Nhận hoa hồng"
                                desc="Khi bạn bè mua hàng qua link, bạn nhận được hoa hồng tại Longhousee"
                            />
                        </div>
                </div>

                {/* Use cases */}
                <div className="bg-surface p-4 mt-2 rounded-lg">
                        <h2 className="font-bold text-base mb-3">Dùng để làm gì?</h2>
                        <div className="grid grid-cols-1 gap-2.5">
                            <UseCase
                                icon={faShoppingBag}
                                color="text-orange-500"
                                bg="bg-orange-50"
                                text="Share deal hot, mã giảm giá lên group"
                            />
                            <UseCase
                                icon={faUsers}
                                color="text-blue-500"
                                bg="bg-blue-50"
                                text="Chia sẻ link live, flash sale cho bạn bè"
                            />
                            <UseCase
                                icon={faCoins}
                                color="text-green-500"
                                bg="bg-green-50"
                                text="Kiếm thêm thu nhập từ việc chia sẻ link mua sắm"
                            />
                        </div>
                </div>

                <div className="mt-2" />
                <FooterV2 />
            </div>
        </div>
    );
}

function StepItem({ step, icon, color, title, desc }) {
    return (
        <div className="flex items-start gap-3">
            <div
                className={`${color} w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0`}
            >
                <FontAwesomeIcon icon={icon} className="text-sm" />
            </div>
            <div>
                <p className="font-semibold text-sm">
                    <span className="text-content-3 mr-1">#{step}</span>
                    {title}
                </p>
                <p className="text-xs text-content-3 mt-0.5">{desc}</p>
            </div>
        </div>
    );
}

function UseCase({ icon, color, bg, text }) {
    return (
        <div className={`flex items-center gap-3 p-3 ${bg} rounded-xl`}>
            <FontAwesomeIcon icon={icon} className={`${color} text-lg`} />
            <span className="text-sm font-medium text-content-2">{text}</span>
        </div>
    );
}

export default LinkConverter;
